import { useMutation } from 'react-query';
import { JsonValue } from 'type-fest';

import {
  useRequest,
  type MutationFunction,
  type RequestQueryObject,
  type Rule,
  type ValidationErrorItem,
  type MutationFunctionContext,
} from '@frontend/api';

import type { RuleDetailsError } from './types';

export type RulesJsonValidateRequestBody =
  | {
      rulesData: Partial<Rule>[];
    }
  | FormData;

type StagedRuleResult = {
  rule: Rule;
  requiresRecalc: boolean;
};

type StagedRuleError = {
  rule: Rule;
  errors: ValidationErrorItem[];
};

export type RulesJsonValidateResult = {
  created: StagedRuleResult[];
  updated: StagedRuleResult[];
  errors: StagedRuleError[];
};

export const useRulesJsonValidate: MutationFunction<
  RulesJsonValidateResult,
  RulesJsonValidateRequestBody,
  [],
  RequestQueryObject,
  RuleDetailsError
> = ({ requestOptions, queryOptions } = {}) => {
  const urlPath = '/rules/json_validate/';
  const request = useRequest(requestOptions);

  return useMutation<
    RulesJsonValidateResult,
    RuleDetailsError,
    RulesJsonValidateRequestBody,
    MutationFunctionContext<RulesJsonValidateResult>
  >(
    (body) => {
      return request<RulesJsonValidateResult & JsonValue>(urlPath, {
        init: {
          method: 'POST',
          body,
        },
      });
    },
    {
      ...queryOptions,
      mutationKey: ['rulesJsonValidate'],
    },
  );
};
